import { useState, type VoidFunctionComponent } from 'react';

import { FormLabelVariant, LabeledTextInput } from '../../generic';
import Button, { LoaderButton } from '../../generic/Button';
import UploadFile from '../../generic/Forms/UploadFile';
import ProgressBar from '../../generic/ProgressBar';
import type { ManuallyAddedDependency, ManuallyAddedDependencyRequest } from '../../../../typings/api';

import type { UploadAndBuildArchiveDependencyParams } from './hooks/useUploadAndBuildArchiveDependency';

import styles from './UploadDependency.module.less';

export type Props = {
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;
  /**
   * Locator of the existing dependency when overwriting.
   */
  overwriteDependencyLocator?: string;

  /**
   * Callback to upload and build an archive.
   * NOTE: The return value here is typed as "unknown" because React Query's mutateAsync
   * returns whatever the mutation function returns but we don't care about it.
   */
  uploadAndBuildDependency?: (params: UploadAndBuildArchiveDependencyParams) => Promise<unknown>;
  /**
   * Whether the dependency is currently being uploaded.
   */
  isUploading?: boolean;

  /**
   * Callback to save the dependency.
   * NOTE: The return value here is typed as "unknown" because React Query's mutateAsync
   * returns whatever the mutation function returns but we don't care about it.
   */
  saveDependency?: (params: ManuallyAddedDependencyRequest) => Promise<unknown>;
  /**
   * Whether the dependency is currently being saved.
   */
  isSaving?: boolean;

  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
};

const UploadDependency: VoidFunctionComponent<Props> = ({
  isVendored,
  overwriteDependencyLocator,
  uploadAndBuildDependency,
  isUploading,
  saveDependency,
  isSaving,
  onClose,
}) => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [url, setURL] = useState<string>('');

  const [filePath, setFilePath] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [homepage, setHomepage] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const [bytesUploaded, setBytesUploaded] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string>('');

  const canSaveFileOrURL = !!file || !!url;
  const canSaveVendored = isVendored ? !!filePath : true;
  const canSave = !!name && !!version && canSaveFileOrURL && canSaveVendored;

  const isUploadingOrSaving = !!isUploading || !!isSaving;

  const onUpload = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    let newDependency: ManuallyAddedDependency | undefined;
    if (url) {
      newDependency = {
        kind: 'url',
        url,
        name,
        version,
        description: description || undefined,
        homepage: homepage || undefined,
        vendoredPath: filePath || undefined,
      };
    } else if (file) {
      newDependency = {
        kind: 'archive',
        name,
        version,
        description: description || undefined,
        homepage: homepage || undefined,
        vendoredPath: filePath || undefined,
      };
    }

    // NOTE: We can only get here if one of "url" or "file" is provided but TS doesn't know that.
    if (!newDependency) {
      return;
    }

    if (file && uploadAndBuildDependency) {
      try {
        await uploadAndBuildDependency({
          file,
          name,
          version,
          homepage: homepage || undefined,
          description: description || undefined,
          onArchiveUploadProgress: (progressEvent) => setBytesUploaded(progressEvent.loaded),
        });
      } catch {
        // Set the upload error state to display an error banner and exit early to prevent saving the dependency.
        setUploadError('There was an error uploading your file. Please, try again.');
        return;
      }

      // Clear any existing upload error when upload succeeds.
      setUploadError('');
    }

    if (overwriteDependencyLocator) {
      await saveDependency?.({
        kind: 'replace',
        overwrittenLocator: overwriteDependencyLocator,
        newDependency,
      });
    } else {
      await saveDependency?.({
        kind: 'new',
        newDependency,
      });
    }

    onClose?.();
  };

  return (
    <section>
      <header>
        <p className={styles.overview}>
          Upload an archive from your computer or a URL to add a {isVendored ? 'vendored' : 'manual'} dependency to your project.
        </p>
      </header>

      <main className={styles.main}>
        <h3 className={styles.title}>
          Choose File
        </h3>
        <UploadFile
          file={file}
          setFile={setFile}
          url={url}
          setURL={setURL}
          isDisabled={isUploadingOrSaving}
        />

        {file && isUploadingOrSaving && (
          <ProgressBar
            bytesFinished={bytesUploaded}
            bytesTotal={file.size}
          />
        )}

        {uploadError && (
          <div className={styles.uploadErrorBanner}>
            {uploadError}
          </div>
        )}

        <h3 className={styles.title}>
          Package Details
        </h3>
        {isVendored && (
          <div className={styles.field}>
            <LabeledTextInput
              id="upload-dependency-file-path"
              size="small"
              label="File Path"
              labelVariant={FormLabelVariant.Filter}
              placeholder="Path to the vendored dependency"
              value={filePath}
              onChange={(value) => setFilePath(value)}
              isDisabled={isUploadingOrSaving}
              isRequired
            />
            <p className={styles.notes}>Add the path to the vendored dependency.</p>
          </div>
        )}

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-name"
            size="small"
            label="Name"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a package name"
            value={name}
            onChange={(value) => setName(value)}
            isDisabled={isUploadingOrSaving}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-version"
            size="small"
            label="Version"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a version number, eg. v1 or 1.0"
            value={version}
            onChange={(value) => setVersion(value)}
            isDisabled={isUploadingOrSaving}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-homepage"
            size="small"
            label="Homepage"
            labelVariant={FormLabelVariant.Filter}
            placeholder="https://dependency.com"
            value={homepage}
            onChange={(value) => setHomepage(value)}
            isDisabled={isUploadingOrSaving}
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-description"
            size="small"
            label="Description"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a description"
            value={description}
            onChange={(value) => setDescription(value)}
            isDisabled={isUploadingOrSaving}
          />
        </div>

        <div className={styles.infoCard}>
          <p className={styles.infoCardHeading}>Uploaded dependencies aren&apos;t validated by FOSSA</p>
          <p className={styles.infoCardText}>
            We scan the uploaded files for licensing data, but package details
            can&apos;t be verified and are saved as provided.
          </p>
        </div>
      </main>

      <footer className={styles.footer}>
        <Button type="secondary" onClick={() => onClose?.()}>Cancel</Button>
        <LoaderButton
          type="primary"
          onClick={onUpload}
          isDisabled={!canSave || isUploadingOrSaving}
          isLoading={isUploadingOrSaving}
        >
          Upload
        </LoaderButton>
      </footer>
    </section>
  );
};

export default UploadDependency;
